import React, { FC, useEffect, useState } from 'react'

import clsx from 'clsx'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

// Components
import MainModal from './common/MainModal'
import { InputUploadMemberPictureRow } from '../inputs/InputUploadMemberPictureRow'
import { IconError } from '../icons/IconError'
import { InputRow } from './components/InputRow'
import { ModalBackButton } from './components/ModalBackButton'
import { HelpRootPageDataPassOn } from '../../pages/help/tabs/HelpRootPagePoliciesTab'

import useToasterHelper from '../../helpers/ToasterHelper'

// Redux
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  riderProfileRegistrationActions,
  selectRiderProfileEventId,
  selectRiderProfileRider,
  selectRiderProfileType,
} from '../../store/riderProfileRegistration/riderProfileRegistrationSlice'

// Types
import { ECompetitorTypes, IRiderSignupForm } from '../../types/competitor_types'

// FakeData
import { PoliciesData } from '../../fakeData/policiesData'

import { riderSignupSchema } from '../../validations'

// Const
import { MODAL_CONSTS } from '../../const/modal-const'
import { MESSAGES_CONST } from '../../const/messages-const'
import { createRiderProfileInputFields } from '../home/modals/data/input-data'
import { authHelpers } from '../../helpers/helpers'
import { useCreate } from '../../hooks/useCreate'

const userIcon = '/assets/img/User2.png'

interface CreateRiderProfileModalProps {
  handleModal: (showHide: boolean, typeOfModal: string, data?: any) => void
}

export const CreateRiderProfileStepTwoModal: FC<CreateRiderProfileModalProps> = ({
  handleModal,
}) => {
  const dispatch = useAppDispatch()
  const toastFunctions = useToasterHelper()

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<IRiderSignupForm>({
    mode: 'onChange',
    resolver: yupResolver(riderSignupSchema),
  })

  const riderProfileRider = useAppSelector(selectRiderProfileRider)
  const riderProfileType = useAppSelector(selectRiderProfileType)
  const riderProfileEventId = useAppSelector(selectRiderProfileEventId)

  const { createRiderProfile } = useCreate()

  const [loading, setLoading] = useState(false)
  const [picture, setPicture] = useState('')
  const [file, setFile] = useState<File | null>(null)

  useEffect(() => {
    if (riderProfileRider && riderProfileRider.signupForm) {
      Object.keys(riderProfileRider.signupForm).forEach((key) => {
        setValue(
          key as keyof IRiderSignupForm,
          riderProfileRider!.signupForm[key as keyof IRiderSignupForm]
        )
      })
    }
    if (riderProfileRider && riderProfileRider.picture) {
      const imageUrl = URL.createObjectURL(riderProfileRider.picture)
      setPicture(imageUrl)
    }
  }, [riderProfileRider])

  const linkClasses = 'cursor-pointer transition-all underline hover:text-SeabiscuitMainThemeColor'

  const cancelHandler = () => {
    dispatch(riderProfileRegistrationActions.clear())
    handleModal(false, MODAL_CONSTS.CREATE_RIDER_PROFILE_STEP_2)
  }

  const handlerImageChanges = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true)

    const files = event.target.files
    const file = files?.[0]
    if (!file) return toastFunctions.info({ message: MESSAGES_CONST.NO_FILE_SELECTED })
    try {
      setFile(file)
      const imageUrl = URL.createObjectURL(file)
      setPicture(imageUrl)
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setLoading(false)
    }
  }

  const openModalPoliciesHandler = (isPrivacyPolicy: boolean) => {
    handleModal(true, MODAL_CONSTS.HELP_VIDEO, {
      component: (
        <HelpRootPageDataPassOn
          handleModal={handleModal}
          policy={PoliciesData[isPrivacyPolicy ? 1 : 0]}
        />
      ),
      small: true,
    })
  }

  const onSubmit = async (formData: IRiderSignupForm) => {
    setLoading(true)
    try {
      if (
        riderProfileType === ECompetitorTypes.spectator ||
        riderProfileType === ECompetitorTypes.guardian
      ) {
        await createRiderProfile({
          riderProfileRider: formData,
          riderProfileEventId,
          imageFile: file,
        })
      } else {
        if (!formData?.fullName.split(' ')[1]) {
          toastFunctions.error({
            message: 'The full name should start with the first name, followed by the last name.',
          })
          return null
        }

        const responseError = await authHelpers.isUsernameUnique(formData?.userName)
        if (responseError) {
          toastFunctions.error({ message: responseError })
          return null
        }

        dispatch(
          riderProfileRegistrationActions.setRiderForm({
            picture: file || riderProfileRider?.picture,
            signupForm: formData,
          })
        )
        handleModal?.(false, MODAL_CONSTS.CREATE_RIDER_PROFILE_STEP_2)
        handleModal?.(true, MODAL_CONSTS.ADD_NEW_HORSE_MEMBER, {
          backHandler: () => {
            handleModal?.(false, MODAL_CONSTS.ADD_NEW_HORSE_MEMBER)
            handleModal(true, MODAL_CONSTS.CREATE_RIDER_PROFILE_STEP_2)
          },
        })
      }
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setLoading(false)
    }
  }

  return (
    <MainModal
      title={
        riderProfileType === ECompetitorTypes.spectator ||
        riderProfileType === ECompetitorTypes.guardian
          ? 'Create your profile'
          : 'Create rider profile'
      }
      description={'Create a profile to streamline all future registrations'}
      show={true}
      goBackBtn={
        <ModalBackButton
          onClick={() => {
            handleModal(false, MODAL_CONSTS.CREATE_RIDER_PROFILE_STEP_2)
            handleModal(true, MODAL_CONSTS.CREATE_RIDER_PROFILE_STEP_1)
          }}
        />
      }
      type="CREATE_RIDER_PROFILE_STEP_2"
      size={'lg'}
      buttons={[
        {
          label:
            riderProfileType === ECompetitorTypes.spectator ||
            riderProfileType === ECompetitorTypes.guardian
              ? 'CREATE'
              : 'NEXT >',
          fullWidth: true,
          loading,
          onClick: (e) => handleSubmit(onSubmit)(e),
        },
        {
          label: 'CANCEL',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          className: 'outline-none',
          loading,
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitLightTextColor',
          fullWidth: true,
          onClick: cancelHandler,
        },
      ]}
    >
      <div className={'-ml-2 -mr-2 flex flex-col gap-3 text-SeabiscuitDark200ThemeColor'}>
        <InputUploadMemberPictureRow
          title={'Profile picture'}
          isLoading={false}
          picture={picture}
          onchangeHandler={handlerImageChanges}
          userIcon={userIcon}
        />

        <form className={'flex flex-col w-full gap-4'}>
          {createRiderProfileInputFields(control).map((field) => (
            <InputRow
              key={field.name}
              field={field}
              errors={errors[field.name]}
              register={register(field.name)}
              control={control}
              placeholder={`Enter ${field.label.toLowerCase()}...`}
            />
          ))}
          <button type={'submit'} className={'!hidden'}>
            Submit
          </button>
        </form>

        <div className={'flex mt-4 gap-4 bg-[#F6F7FB] py-[12px] px-[20px] rounded-[12px]'}>
          <div className={'mt-1'}>
            <IconError />
          </div>

          <p className={'flex-1 text-[14px] pr-8'}>
            By creating a profile you are agreeing to our{' '}
            <span className={clsx(linkClasses)} onClick={() => openModalPoliciesHandler(false)}>
              Terms and Conditions
            </span>{' '}
            and{' '}
            <span className={clsx(linkClasses)} onClick={() => openModalPoliciesHandler(true)}>
              Privacy Policy
            </span>
          </p>
        </div>
      </div>
    </MainModal>
  )
}
